import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BreadcrumbItem } from '@/components/navigation/BreadcrumbNav';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noIndex?: boolean;
  keywords?: string[];
  canonical?: string;
  // Structured data
  structuredData?: {
    type: string;
    name?: string;
    description?: string;
    image?: string;
    url?: string;
    latitude?: number;
    longitude?: number;
  };
  // Breadcrumb structured data
  breadcrumbs?: BreadcrumbItem[];
}

export const SEO = ({
  title,
  description,
  image = '/og-image.png',
  url,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  noIndex = false,
  keywords,
  canonical,
  structuredData,
  breadcrumbs
}: SEOProps) => {
  // Build the full title with site name
  const fullTitle = title ? `${title} | travilis` : 'travilis - Your Travel Companion';
  
  // Get current URL if not provided
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  
  // Default meta description if not provided
  const metaDescription = description || 'Connect with amazing local hosts around the world and find your perfect travel experiences with travilis.';
  
  // Canonical URL
  const canonicalUrl = canonical || currentUrl;

  // Generate structured data JSON if provided
  let structuredDataJSON = null;
  
  if (structuredData && structuredData.type === 'TouristDestination') {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    
    // Ensure image URL is absolute
    const imageUrl = structuredData.image 
      ? (structuredData.image.startsWith('http') 
          ? structuredData.image 
          : `${origin}${structuredData.image}`)
      : null;
          
    structuredDataJSON = {
      '@context': 'https://schema.org',
      '@type': 'TouristDestination',
      name: structuredData.name,
      description: structuredData.description,
      url: structuredData.url || currentUrl,
      ...(imageUrl && { image: imageUrl }),
      ...(structuredData.latitude && structuredData.longitude && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: structuredData.latitude,
          longitude: structuredData.longitude
        }
      })
    };
  }
  
  // Generate breadcrumb structured data if provided
  let breadcrumbJSON = null;
  
  if (breadcrumbs && breadcrumbs.length > 0) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    
    breadcrumbJSON = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.url.startsWith('http') ? item.url : `${origin}${item.url}`
      }))
    };
  }

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords.join(', ')} />}
      {noIndex && <meta name="robots" content="noindex" />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Sitemap reference */}
      <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image.startsWith('http') ? image : `${typeof window !== 'undefined' ? window.location.origin : ''}${image}`} />
      <meta property="og:site_name" content="travilis" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image.startsWith('http') ? image : `${typeof window !== 'undefined' ? window.location.origin : ''}${image}`} />
      
      {/* Article specific meta tags */}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === 'article' && author && <meta property="article:author" content={author} />}
      
      {/* JSON-LD Structured Data */}
      {structuredDataJSON && (
        <script type="application/ld+json">
          {JSON.stringify(structuredDataJSON)}
        </script>
      )}
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbJSON && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJSON)}
        </script>
      )}
    </Helmet>
  );
};
